import { Button } from '@heidi/design-system/button';
import { ChevronsUp, ChevronsUpDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { WrittenObjectKind } from '../../../../data/projects/timeline/object-card/objectWrites';

const VISIBLE_ROWS = 10;

const seeMoreMessages = defineMessages({
  patient: {
    defaultMessage: 'See {count} more {count, plural, one {patient} other {patients}}',
    id: 'eE8BaevZCg',
  },
  session: {
    defaultMessage: 'See {count} more {count, plural, one {session} other {sessions}}',
    id: '1CQi8JcPHx',
  },
  task: {
    defaultMessage: 'See {count} more {count, plural, one {task} other {tasks}}',
    id: 'kRw2YITkxf',
  },
  document: {
    defaultMessage: 'See {count} more {count, plural, one {document} other {documents}}',
    id: 'op/6ifsmca',
  },
  template: {
    defaultMessage: 'See {count} more {count, plural, one {template} other {templates}}',
    id: 'ygpvP5GKb9',
  },
  routine: {
    defaultMessage: 'See {count} more {count, plural, one {routine} other {routines}}',
    id: 'pqmUy17nsL',
  },
});

const messages = defineMessages({
  seeLess: { defaultMessage: 'See less', id: 'GZo7vgc3wE' },
});

export interface ObjectListFrameProps<T> {
  kind: WrittenObjectKind;
  items: readonly T[];
  renderRow: (item: T) => ReactNode;
}

export function ObjectListFrame<T>({
  kind,
  items,
  renderRow,
}: ObjectListFrameProps<T>): JSX.Element {
  const intl = useIntl();
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, VISIBLE_ROWS);
  const hiddenCount = items.length - visible.length;

  return (
    <div
      className="w-full min-w-0 overflow-hidden rounded-lg border border-border bg-surface"
      data-testid="object-list"
      data-kind={kind}
    >
      <div className="p-1">
        <ul className="flex flex-col">{visible.map(renderRow)}</ul>
        {items.length > VISIBLE_ROWS ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setExpanded((previous) => !previous)}
            className="w-full cursor-default justify-start rounded-md px-3 text-start text-text-secondary hover:text-text-primary"
          >
            {expanded ? (
              <ChevronsUp className="size-4 shrink-0" aria-hidden />
            ) : (
              <ChevronsUpDown className="size-4 shrink-0" aria-hidden />
            )}
            {expanded
              ? intl.formatMessage(messages.seeLess)
              : intl.formatMessage(seeMoreMessages[kind], { count: hiddenCount })}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
